import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/site";
import { useSettings } from "@/context/SettingsContext";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487 2.981 1.287 2.981.858 3.518.804.537-.054 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /></svg>
);

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => { document.title = "İletişim | VoyaCar Rental"; }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Ad soyad gerekli";
    if (!form.phone.trim()) err.phone = "Telefon gerekli";
    if (!form.subject.trim()) err.subject = "Konu gerekli";
    if (!form.message.trim()) err.message = "Mesaj gerekli";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) err.email = "Geçerli e-posta girin";
    setErrors(err);
    if (Object.keys(err).length) return;
    setSending(true);
    try {
      await api.post("/contact", { ...form, email: form.email || null });
      toast.success("Mesajınız alındı, en kısa sürede dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Bir hata oluştu, tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  const waUrl = buildWhatsAppUrl(settings.whatsapp, "Merhaba VoyaCar Rental, bilgi almak istiyorum.");

  return (
    <div>
      <div className="bg-[#171717] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-bold">İletişim</h1>
          <p className="mt-2 text-neutral-300">Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-4">
              <a href={`tel:${settings.phone_link}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                <div className="h-11 w-11 rounded-xl bg-[#FFF0F2] flex items-center justify-center"><Phone className="h-5 w-5 text-[#A11016]" /></div>
                <div><div className="text-xs text-neutral-500">Telefon</div><div className="font-semibold text-[#171717] group-hover:text-[#A11016]">{settings.phone}</div></div>
              </a>
              <a href={waUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
                <div className="h-11 w-11 rounded-xl bg-[#E9FBEF] flex items-center justify-center text-[#25D366]"><WhatsAppIcon className="h-5 w-5" /></div>
                <div><div className="text-xs text-neutral-500">WhatsApp</div><div className="font-semibold text-[#171717] group-hover:text-[#25D366]">{settings.phone}</div></div>
              </a>
              <a href={`mailto:${settings.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="h-11 w-11 rounded-xl bg-[#FFF0F2] flex items-center justify-center"><Mail className="h-5 w-5 text-[#A11016]" /></div>
                <div><div className="text-xs text-neutral-500">E-posta</div><div className="font-semibold text-[#171717] group-hover:text-[#A11016]">{settings.email}</div></div>
              </a>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#FFF0F2] flex items-center justify-center"><MapPin className="h-5 w-5 text-[#A11016]" /></div>
                <div><div className="text-xs text-neutral-500">Hizmet Bölgesi</div><div className="font-semibold text-[#171717]">{settings.service_city}</div></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#FFF0F2] flex items-center justify-center"><Clock className="h-5 w-5 text-[#A11016]" /></div>
                <div><div className="text-xs text-neutral-500">Çalışma Saatleri</div><div className="font-semibold text-[#171717]">{settings.working_hours}</div></div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-neutral-200 h-[280px]">
              <iframe title="Harita" src="https://www.google.com/maps?q=Atatürk+Mahallesi+Alemdağ+Caddesi+Çeşme+Sokak+No+3+Ümraniye+İstanbul&output=embed" className="w-full h-full" loading="lazy" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-[#171717] mb-5">Bize Yazın</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label className="text-sm mb-1.5 block">Ad Soyad *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
                {errors.name && <p className="text-xs text-[#A11016] mt-1">{errors.name}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Telefon *</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" />
                  {errors.phone && <p className="text-xs text-[#A11016] mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">E-posta</Label>
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" />
                  {errors.email && <p className="text-xs text-[#A11016] mt-1">{errors.email}</p>}
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Konu *</Label>
                <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" />
                {errors.subject && <p className="text-xs text-[#A11016] mt-1">{errors.subject}</p>}
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Mesaj *</Label>
                <Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
                {errors.message && <p className="text-xs text-[#A11016] mt-1">{errors.message}</p>}
              </div>
              <Button type="submit" disabled={sending} className="w-full h-12 bg-[#A11016] hover:bg-[#820D12] text-white font-semibold" data-testid="contact-submit">{sending ? "Gönderiliyor..." : "Mesajı Gönder"}</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
